import React, { useState, useEffect } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, ReceiptIcon, ArrowLeft, ChevronRight, CalendarIcon, Search, Filter, CheckIcon, ClipboardCheck, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { eventService } from '@/services/eventService';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import SubscriptionRequiredModal from '@/components/payment/SubscriptionRequiredModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { useIsMobile } from '@/hooks/use-mobile';

interface PendingBilling {
  studentId: string;
  studentName: string;
  eventCount: number;
  totalAmount: number;
  lastEventDate: string;
}

const InvoicesBilling = () => {
  const { t, locale } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudent, setSelectedStudent] = useState<any | undefined>(undefined);
  const [selectedMonth, setSelectedMonth] = useState<Date | undefined>(undefined);
   const isMobile = useIsMobile();

  // Check if user has subscription or is in trial period
  const hasAccess = user?.is_subscribed || user?.is_trialing;

  useEffect(() => {
    // If user doesn't have access, show the subscription modal
    if (!hasAccess) {
      setShowSubscriptionModal(true);
    }
  }, [hasAccess]);

  const { data: pendingBillings = [], isLoading: pendingBillingsLoading } = useQuery({
    queryKey: ['pendingBillings', token],
    queryFn: () => eventService.getPendingBillings(token),
    // Only fetch data if user has access
    enabled: !!token && hasAccess,
  });

  const { data: pendingValidationEvents = [], isLoading: pendingValidationLoading } = useQuery({
    queryKey: ['pendingValidationEvents', token],
    queryFn: () => eventService.getPendingValidationEvents(token),
    enabled: !!token && hasAccess,
  });

  const pendingValidationCount = React.useMemo(() => {
    // Group by event id since multiple rows can share the same event
    const uniqueEventIds = new Set<string>();
    pendingValidationEvents.forEach((event: any) => {
      if (event.id) uniqueEventIds.add(String(event.id));
    });
    return uniqueEventIds.size;
  }, [pendingValidationEvents]);

  const hasPendingValidation = pendingValidationCount > 0;


  const { data: students = [], isLoading: studentsLoading } = useQuery({
    queryKey: ['students', token],
    queryFn: () => eventService.getStudents(user, token),
  });

  const translateStr = (key: string, options?: Record<string, any>): string => {
    const translated = t(key, options);
    return Array.isArray(translated) ? translated.join(' ') : translated;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: user.currency_code || '$'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'en-US').format(new Date(dateString));
  };

  const handleStudentClick = (studentId: string) => {
    navigate(`/invoices/create/${studentId}`);
  };

  const filteredBillings = React.useMemo(() => {
    if (pendingBillingsLoading || !pendingBillings) return [];

    return pendingBillings.filter((billing: PendingBilling) => {
      let passes = true;

      if (searchTerm) {
        passes = billing.studentName.toLowerCase().includes(searchTerm.toLowerCase());
      }

      if (passes && selectedStudent) {
        const searchTerm = `${selectedStudent.firstname} ${selectedStudent.lastname}`.toLowerCase();
        const studentName = billing.studentName.toLowerCase();
        passes = studentName.includes(searchTerm) || studentName.includes(selectedStudent.firstname.toLowerCase());
      }

      if (passes && selectedMonth) {
        const billingDate = new Date(billing.lastEventDate);
        passes = (
          billingDate.getMonth() === selectedMonth.getMonth() &&
          billingDate.getFullYear() === selectedMonth.getFullYear()
        );
      }

      return passes;
    });
  }, [pendingBillings, pendingBillingsLoading, searchTerm, selectedStudent, selectedMonth]);

  const resetFilters = () => {
    setSelectedStudent(undefined);
    setSelectedMonth(undefined);
    setSearchTerm("");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Subscription required modal */}
      <SubscriptionRequiredModal
        isOpen={showSubscriptionModal}
        onClose={() => {
          setShowSubscriptionModal(false);
          if (!hasAccess) {
            navigate('/invoices');
          }
        }}
      />

      <main className="flex-1 container py-8 mt-16">
        {hasAccess ? (
          <>
            <div>
              <Button
                variant="ghost"
                onClick={() => navigate('/invoices')}
                className="mb-4 bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {translateStr('common.back')}
              </Button>
            </div>

            {/* Prominent pending validation banner */}
            {hasPendingValidation && (
              <div className="mb-6 overflow-hidden rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/40 dark:border-amber-800 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 sm:p-5">
                  <div className="flex items-center gap-3">
                    {/* L'icône est cachée sur mobile et s'affiche à partir de l'écran 'sm' */}
                    <div className="hidden sm:flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                      <ClipboardCheck className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-amber-900 dark:text-amber-100">
                        {translateStr('invoices.pendingValidationBannerTitle')}
                      </h3>
                      <p className="text-sm text-amber-800 dark:text-amber-200/80">
                        {translateStr('invoices.pendingValidationBannerDescription', { count: pendingValidationCount })}
                      </p>
                    </div>
                  </div>
                  
                  {/* Passage en colonne sur mobile, largeur 100% */}
                  <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto sm:ml-auto">
                    {/*<Badge 
                      variant="outline" 
                      className="w-full sm:w-auto justify-center bg-white/60 dark:bg-black/20 border-amber-300 text-amber-900 dark:text-amber-100 px-2.5 py-1"
                    >
                      {pendingValidationCount} {pendingValidationCount === 1 ? translateStr('invoices.pendingValidationEventSingular') : translateStr('invoices.pendingValidationEventPlural')}
                    </Badge>*/}
                    <Button
                      size="sm"
                      onClick={() => navigate('/invoices/pending-validation')}
                      className="w-full sm:w-auto justify-center bg-amber-600 hover:bg-amber-700 text-white dark:bg-amber-600 dark:hover:bg-amber-500"
                    >
                      {translateStr('invoices.pendingValidation')}
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

          <Card className="border-0 rounded-none shadow-none p-1 md:border md:rounded-xl md:shadow-sm md:p-0">
            <CardHeader className="p-0 md:p-6">

                 <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-4">
                  <CardTitle>{translateStr('invoices.pendingBillings')}</CardTitle>
                </div>

                {/* <CardDescription>
                  {translateStr('invoices.pendingBillingsDescription')}
                </CardDescription> */}
                <div className={`flex ${isMobile ? 'flex-col gap-3' : 'flex-row items-center gap-4 justify-between'} bg-muted/50 rounded-md border p-3 px-4`}>
                  <div className="flex flex-1 gap-2">
                    <div className="relative md:w-56 w-full">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="search"
                        placeholder={translateStr('students.search')}
                        className="pl-8"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="icon">
                          <Filter className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[220px]">
                        <DropdownMenuLabel>{translateStr('invoices.filters')}</DropdownMenuLabel>
                        <DropdownMenuSeparator />

                        <div className="p-2">
                          <div className="mb-2">
                            <p className="text-xs mb-1 font-medium">{translateStr('invoices.student')}</p>
                            <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                              <SelectTrigger className="w-full h-8 text-xs">
                                <SelectValue placeholder={translateStr('invoices.filter.allStudents')} />
                              </SelectTrigger>
                              <SelectContent>
                                {students.map((student: any) => (
                                  <SelectItem key={student.id} value={student}>
                                    {student.firstname} {student.lastname}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="mb-2">
                            <p className="text-xs mb-1 font-medium">{translateStr('invoices.filter.date')}</p>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  className="w-full h-8 text-xs justify-start font-normal"
                                  size="sm"
                                >
                                  {selectedMonth ? (
                                    format(selectedMonth, 'MMMM yyyy')
                                  ) : (
                                    <span>{translateStr('invoices.filter.selectDate')}</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <CalendarComponent
                                  mode="single"
                                  selected={selectedMonth}
                                  onSelect={setSelectedMonth}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </div>

                          <Button className="w-full mt-2" size="sm" variant="ghost" onClick={resetFilters}>
                            {translateStr('invoices.filter.resetFilters')}
                          </Button>
                        </div>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0 pt-2 md:p-6 md:pt-0">
                {pendingBillingsLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-4">
                        <Skeleton className="h-12 w-12 rounded-full" />
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-[250px]" />
                          <Skeleton className="h-4 w-[200px]" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : filteredBillings.length > 0 ? (
                  <>
                    {/* 📱 VUE MOBILE : Liste de cartes (cachée sur md+) */}
                    <div className="space-y-3 md:hidden">
                      {filteredBillings.map((billing) => (
                        <div
                          key={billing.studentId}
                          className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-sm active:bg-muted/50 transition-colors"
                          onClick={() => handleStudentClick(billing.studentId)}
                        >
                          {/* Ligne du haut : Nom + Montant Estimé */}
                          <div className="flex items-start justify-between gap-2">
                            <span className="font-semibold text-foreground text-base line-clamp-1">
                              {billing.studentName}
                            </span>
                            <span className="font-bold text-base text-right shrink-0">
                              {formatCurrency(billing.totalAmount)}
                            </span>
                          </div>

                          {/* Ligne du milieu : Infos secondaires (Badge events + Date de dernier event) */}
                          <div className="flex items-center justify-between text-xs text-muted-foreground gap-2">
                            <Badge variant="outline" className="bg-blue-50/50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/50">
                              {billing.eventCount} {translateStr('invoices.events')}
                            </Badge>
                            <div className="flex items-center gap-1">
                              <CalendarIcon className="h-3.5 w-3.5" />
                              <span>{formatDate(billing.lastEventDate)}</span>
                            </div>
                          </div>

                          {/* Bouton d'action rapide sur mobile, pleine largeur */}
                          <Button 
                            size="sm" 
                            className="w-full mt-1 justify-center"
                            onClick={(e) => {
                              // Empêche le clic de se propager à la carte parent si besoin
                              e.stopPropagation(); 
                              handleStudentClick(billing.studentId);
                            }}
                          >
                            <span>{translateStr('invoices.createInvoice')}</span>
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </div>
                      ))}
                    </div>

                    {/* 💻 VUE TABLETTE/DESKTOP : Tableau classique (caché sur mobile) */}
                    <div className="hidden md:block rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{translateStr('invoices.student')}</TableHead>
                            <TableHead>{translateStr('invoices.pendingEvents')}</TableHead>
                            <TableHead>{translateStr('invoices.lastEventDate')}</TableHead>
                            <TableHead className="text-right">{translateStr('invoices.estimatedAmount')}</TableHead>
                            <TableHead className="w-[100px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredBillings.map((billing) => (
                            <TableRow 
                              key={billing.studentId} 
                              className="cursor-pointer hover:bg-muted/50" 
                              onClick={() => handleStudentClick(billing.studentId)}
                            >
                              <TableCell className="font-medium">{billing.studentName}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 hover:bg-blue-100">
                                  {billing.eventCount} {translateStr('invoices.events')}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                                  {formatDate(billing.lastEventDate)}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                {formatCurrency(billing.totalAmount)}
                              </TableCell>
                              <TableCell>
                                <Button size="sm" className="w-full">
                                  <span>{translateStr('invoices.createInvoice')}</span>
                                  <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </>
                ) : (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {translateStr('invoices.noPendingBillings')}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </>
        ) : null}
      </main>

      <Footer />
    </div>
  );
};

export default InvoicesBilling;
